import type {
  ApiError,
  AuthTokens,
  CatalogApp,
  ConsentContext,
  ConsentDecisionResponse,
  EntitledApp,
  LoginResponse,
  MyConsents,
  OAuthGrant,
  OrgMember,
  OrgStats,
  SelfServiceDataRightsRequest,
  SelfServiceDataRightsRequestAdmin,
  SelfServiceRequestStatus,
  SelfServiceRequestType,
  User,
} from '@/types';
import { Application, Organisation, Membership } from '@/types';

const API_BASE = process.env.NEXT_PUBLIC_API_URL ?? 'http://172.16.17.59:8000/api/v1';

class ApiClient {
  private accessToken: string | null = null;
  private refreshCallback: (() => Promise<boolean>) | null = null;
  // Deduplicate concurrent refresh attempts — all concurrent 401s share one promise
  private refreshPromise: Promise<boolean> | null = null;

  setAccessToken(token: string | null) {
    this.accessToken = token;
  }

  setRefreshCallback(cb: () => Promise<boolean>) {
    this.refreshCallback = cb;
  }

  private async request<T>(
    path: string,
    options: RequestInit = {},
    isRetry = false,
  ): Promise<T> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(options.headers as Record<string, string>),
    };

    if (this.accessToken) {
      headers['Authorization'] = `Bearer ${this.accessToken}`;
    }

    const res = await fetch(`${API_BASE}${path}`, {
      ...options,
      headers,
    });

    // Auto-refresh on 401 — deduplicate concurrent attempts, retry once with new token
    if (res.status === 401 && !isRetry && this.refreshCallback) {
      if (!this.refreshPromise) {
        this.refreshPromise = this.refreshCallback().finally(() => {
          this.refreshPromise = null;
        });
      }
      const refreshed = await this.refreshPromise;
      if (refreshed) return this.request<T>(path, options, true);
    }

    if (!res.ok) {
      const body = await res.json().catch(() => null);
      // Backend wraps errors as { success: false, error: { message, code } }
      const message =
        (body as { error?: { message?: string } } | null)?.error?.message ??
        (body as { message?: string } | null)?.message ??
        res.statusText;
      const code = (body as { error?: { code?: string } } | null)?.error?.code;
      throw { statusCode: res.status, message, code } as ApiError;
    }

    // 204 No Content
    if (res.status === 204) return undefined as T;

    const json = await res.json();
    // Unwrap ResponseInterceptor envelope: { success: true, data: ... }
    if (json && typeof json === 'object' && json.success === true && 'data' in json) {
      return json.data as T;
    }
    return json as T;
  }

  // Auth
  register(body: {
    firstName: string;
    lastName: string;
    orgName: string;
    phone?: string;
    email: string;
    password: string;
    termsConsent: boolean;
    dataConsent: boolean;
    ageConfirmed: boolean;
    marketingConsent: boolean;
    consentVersion: string;
    language: string;
  }) {
    return this.request<LoginResponse>('/auth/register', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  verifyEmail(token: string) {
    return this.request<LoginResponse>('/auth/verify-email', {
      method: 'POST',
      body: JSON.stringify({ token }),
    });
  }

  resendVerification(email: string, orgSlug: string) {
    return this.request<{ message: string }>('/auth/resend-verification', {
      method: 'POST',
      body: JSON.stringify({ email, orgSlug }),
    });
  }

  sendOtp(email: string) {
    return this.request<{ message: string }>('/auth/send-otp', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  }

  verifyOtp(email: string, otp: string) {
    return this.request<{ message: string }>('/auth/verify-otp', {
      method: 'POST',
      body: JSON.stringify({ email, otp }),
    });
  }

  getAppInfo(slug: string) {
    return this.request<{ name: string; logoUrl: string | null }>(`/auth/app-info?slug=${encodeURIComponent(slug)}`);
  }

  login(body: { email: string; password: string }) {
    return this.request<LoginResponse>('/auth/login', {
      method: 'POST',
      body: JSON.stringify(body),
    }, true);  // isRetry=true: credential error, never token expiry — must not trigger auto-refresh
  }

  ssoLogin(body: { email: string; password: string; appSlug: string; state?: string }) {
    return this.request<{ redirectUrl: string }>('/auth/login/sso', {
      method: 'POST',
      body: JSON.stringify(body),
    }, true);  // isRetry=true: credential error, never token expiry — must not trigger auto-refresh
  }

  exchangeSsoCode(code: string, appSlug: string) {
    return this.request<AuthTokens>('/auth/token', {
      method: 'POST',
      body: JSON.stringify({ code, appSlug }),
    });
  }

  refresh(refreshToken: string) {
    return this.request<AuthTokens>('/auth/refresh', {
      method: 'POST',
      body: JSON.stringify({ refreshToken }),
    }, true);  // isRetry=true: prevent 401 on refresh from triggering another refresh
  }

  logout(refreshToken: string) {
    return this.request<void>('/auth/logout', {
      method: 'POST',
      body: JSON.stringify({ refreshToken }),
    }, true);  // isRetry=true: access token may be expired/banned — must not trigger refresh loop
  }

  logoutAll() {
    return this.request<void>('/auth/logout-all', { method: 'POST' }, true);  // isRetry=true: same reason
  }

  getMe(isRetry = false) {
    // Callers inside the refresh flow (refreshAuth) pass isRetry=true so a 401
    // here cannot re-enter the auto-refresh loop and turn a transient token
    // expiry into a spurious logout. Standalone callers keep the default so a
    // genuinely stale token still triggers one silent refresh.
    return this.request<User>('/auth/me', {}, isRetry);
  }

  forgotPassword(email: string) {
    return this.request<{ message: string; recipient?: 'self' | 'org_owner' }>('/auth/forgot-password', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  }

  resetPassword(token: string, newPassword: string) {
    return this.request<{ message: string }>('/auth/reset-password', {
      method: 'POST',
      body: JSON.stringify({ token, newPassword }),
    });
  }

  changePassword(currentPassword: string, newPassword: string) {
    return this.request<{ message: string }>('/auth/change-password', {
      method: 'PATCH',
      body: JSON.stringify({ currentPassword, newPassword }),
    });
  }

  // Self-service profile update (name + phone). Email/role/status stay admin-managed.
  updateProfile(body: { firstName?: string; lastName?: string; phone?: string | null }) {
    return this.request<User>('/auth/profile', {
      method: 'PATCH',
      body: JSON.stringify(body),
    });
  }

  // Upload the current user's avatar (multipart). Bypasses request() so the browser
  // sets the multipart boundary itself; returns the updated user with avatarUrl.
  async uploadAvatar(file: File): Promise<User> {
    const form = new FormData();
    form.append('avatar', file);
    const headers: Record<string, string> = {};
    if (this.accessToken) headers['Authorization'] = `Bearer ${this.accessToken}`;
    const res = await fetch(`${API_BASE}/auth/profile/avatar`, {
      method: 'POST',
      headers,
      body: form,
    });
    if (!res.ok) {
      const body = await res.json().catch(() => null);
      const message =
        (body as { error?: { message?: string } } | null)?.error?.message ??
        (body as { message?: string } | null)?.message ??
        res.statusText;
      throw { statusCode: res.status, message } as ApiError;
    }
    const json = await res.json();
    if (json && typeof json === 'object' && json.success === true && 'data' in json) {
      return json.data as User;
    }
    return json as User;
  }

  // Remove the current user's avatar; returns the updated user (avatarUrl null).
  async deleteAvatar(): Promise<User> {
    return this.request<User>('/auth/profile/avatar', { method: 'DELETE' });
  }

  // Applications (PLATFORM_ADMIN only)
  getApplications(params?: { page?: number; limit?: number; isActive?: boolean }) {
    const q = new URLSearchParams();
    if (params?.page) q.set('page', String(params.page));
    if (params?.limit) q.set('limit', String(params.limit));
    if (params?.isActive !== undefined) q.set('isActive', String(params.isActive));
    return this.request<{ data: Application[]; total: number; page: number; limit: number }>(`/applications?${q}`);
  }

  createApplication(body: {
    name: string; slug: string; description?: string;
    callbackUrl: string; isActive?: boolean;
    isPublished?: boolean;
  }) {
    return this.request<Application>('/applications', { method: 'POST', body: JSON.stringify(body) });
  }

  updateApplication(id: string, body: { name?: string; description?: string; callbackUrl?: string, isPublished?: boolean, minSeats?: number, pricePerSeat?: number | null }) {
    return this.request<Application>(`/applications/${id}`, { method: 'PATCH', body: JSON.stringify(body) });
  }

  activateApplication(id: string) {
    return this.request<Application>(`/applications/${id}/activate`, { method: 'PATCH', body: '{}' });
  }

  deactivateApplication(id: string) {
    return this.request<Application>(`/applications/${id}/deactivate`, { method: 'PATCH', body: '{}' });
  }

  deleteApplication(id: string) {
    return this.request<void>(`/applications/${id}`, { method: 'DELETE' });
  }

  getMemberships(params?: { page?: number; limit?: number; orgId?: string; applicationId?: string; userId?: string }) {
    const q = new URLSearchParams();
    if (params?.page) q.set('page', String(params.page));
    if (params?.limit) q.set('limit', String(params.limit));
    if (params?.orgId) q.set('orgId', params.orgId);
    if (params?.applicationId) q.set('applicationId', params.applicationId);
    if (params?.userId) q.set('userId', params.userId);
    return this.request<{ data: Membership[]; total: number; page: number; limit: number; pages: number; hasNext: boolean; hasPrev: boolean }>(`/memberships?${q}`);
  }

  getUserMemberships(userId: string) {
    return this.request<{ data: Membership[]; total: number }>(`/memberships/user/${userId}`);
  }

  grantMembership(body: { orgId: string; applicationId: string; userId?: string }) {
    return this.request<Membership>('/memberships', { method: 'POST', body: JSON.stringify(body) });
  }

  revokeMembership(id: string) {
    return this.request<void>(`/memberships/${id}`, { method: 'DELETE' });
  }

  deleteUser(id: string) {
    return this.request<void>(`/users/${id}`, { method: 'DELETE' });
  }

  updateUser(id: string, body: { status?: string; role?: string; firstName?: string; lastName?: string }) {
    return this.request<void>(`/users/${id}`, { method: 'PATCH', body: JSON.stringify(body) });
  }

  getUsers(params?: { page?: number; limit?: number; orgId?: string; search?: string }) {
    const q = new URLSearchParams();
    if (params?.page) q.set('page', String(params.page));
    if (params?.limit) q.set('limit', String(params.limit));
    if (params?.orgId) q.set('orgId', params.orgId);
    if (params?.search) q.set('search', params.search);
    return this.request<{ data: User[]; total: number; page: number; limit: number; pages: number; hasNext: boolean; hasPrev: boolean }>(`/users?${q}`);
  }

  /** Public app catalog — used at registration step 2. No auth needed. */
  getCatalog() {
    return this.request<{ apps: CatalogApp[] }>('/auth/catalog');
  }

  /** Set the org's entitled apps (called after register, before redirect). */
  setOrgEntitlement(body: { apps: string[] }) {
    return this.request<{ apps: EntitledApp[] }>('/auth/org-entitlement', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  /** Get the org's entitled apps — for the invite form app picker. */
  getOrgEntitlement() {
    return this.request<{ apps: EntitledApp[] }>('/auth/org-entitlement');
  }

  /** Update seat counts for the org's entitled apps. slug → count map. */
  updateOrgLicences(seats: Record<string, number>) {
    return this.request<{ updated: number }>('/auth/org-licences', {
      method: 'PATCH',
      body: JSON.stringify({ seats }),
    });
  }

  /** Set the org's total purchased seat count (org-wide cap). */
  setOrgSeats(totalSeats: number) {
    return this.request<{ totalSeats: number }>('/auth/org-seats', {
      method: 'PATCH',
      body: JSON.stringify({ totalSeats }),
    });
  }

  /** Org summary stats: memberCount, pendingCount, appCount, totalSeats. */
  getOrgStats() {
    return this.request<OrgStats>('/auth/org-stats');
  }

  /** Org details: id, name, slug, status. */
  getOrg() {
    return this.request<{ id: string; name: string; slug: string; status: string }>('/auth/org');
  }

  /** Invite members, each with their own app selection. */
  inviteMembers(body: {
    members: Array<{ name: string; email: string; apps: string[] }>;
    authorizationConfirmed: boolean;
  }) {
    return this.request<{ invited: Array<{ email: string; name: string }>; skipped: Array<{ email: string; reason: string }> }>(
      '/auth/invite-member',
      { method: 'POST', body: JSON.stringify(body) },
    );
  }

  /** All org members, optionally filtered to those with an active grant for appSlug. */
  getOrgMembers(appSlug?: string) {
    const q = appSlug ? `?appSlug=${encodeURIComponent(appSlug)}` : '';
    return this.request<OrgMember[]>(`/users/org-members${q}`);
  }

  /**
   * Org members with per-user app grants — paginated and server-side searchable.
   *
   * `state` restricts the page to one membership state, so a tabbed list
   * paginates inside its own tab. `meta.total` then counts that state, while
   * `meta.activeTotal` / `meta.inactiveTotal` always count the whole org and
   * stay consistent with the returned rows because they come from one request.
   */
  getOrgMembersWithApps(params?: { page?: number; limit?: number; search?: string; state?: 'active' | 'inactive' }) {
    const q = new URLSearchParams();
    if (params?.page)   q.set('page',   String(params.page));
    if (params?.limit)  q.set('limit',  String(params.limit));
    if (params?.search) q.set('search', params.search);
    if (params?.state)  q.set('state',  params.state);
    const qs = q.toString() ? `?${q.toString()}` : '';
    return this.request<{
      users: import('@/types').OrgMemberWithApps[];
      meta: { total: number; page: number; limit: number; pages: number; activeTotal: number; inactiveTotal: number };
    }>(`/users/org-members-with-apps${qs}`);
  }

  /** Replace a member's active app grants. Pass app slugs the user should have access to. */
  updateUserApps(userId: string, apps: string[]) {
    return this.request<{ updated: boolean }>(`/users/${userId}/apps`, {
      method: 'PATCH',
      body: JSON.stringify({ apps }),
    });
  }

  resetMemberPassword(email: string) {
    return this.request<{ message: string }>('/auth/reset-member-password', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  }

  /** Resend invite credentials — generates a fresh temp password, emails the member, and returns the credentials for admin copy. */
  resendInvitation(email: string, appSlug?: string) {
    return this.request<{ message: string; memberEmail: string; memberName: string; temporaryPassword: string }>('/auth/resend-invitation', {
      method: 'POST',
      body: JSON.stringify({ email, ...(appSlug ? { appSlug } : {}) }),
    });
  }

  /** GET /licences/pool — entitled apps with purchasedLicenses / usedSeats / availableSeats. */
  getLicencePool() {
    return this.request<{ pool: import('@/types').AppLicencePoolRow[] }>('/licences/pool');
  }

  /** PATCH /licences/purchase — set purchased seat counts. { appSlug: count } map. */
  purchaseLicenses(seats: Record<string, number>) {
    return this.request<{ updated: Array<{ slug: string; purchased: number }> }>('/licences/purchase', {
      method: 'PATCH',
      body: JSON.stringify({ seats }),
    });
  }

  /** POST /licences/attach/:userId/:appSlug — assign a seat to a user (EC-001/002/003). */
  attachSeat(userId: string, appSlug: string) {
    // Fastify requires non-empty body when Content-Type: application/json is set
    return this.request<{ assigned: boolean }>(`/licences/attach/${userId}/${appSlug}`, { method: 'POST', body: '{}' });
  }

  /** POST /licences/attach-bulk — assign apps to users all-or-nothing (fails whole batch if seats are short). */
  attachSeatsBulk(userIds: string[], appSlugs: string[]) {
    return this.request<{ assigned: number; skipped: number; users: number; apps: string[] }>('/licences/attach-bulk', {
      method: 'POST',
      body: JSON.stringify({ userIds, appSlugs }),
    });
  }

  /** DELETE /licences/detach/:userId/:appSlug — revoke a user's seat. */
  detachSeat(userId: string, appSlug: string) {
    return this.request<{ detached: boolean }>(`/licences/detach/${userId}/${appSlug}`, { method: 'DELETE' });
  }

  /** GET /addons/:slug/balance — current credit balance for an addon */
  getAddonBalance(slug: string) {
    return this.request<import('@/types').AddonCreditBalance>(`/addons/${slug}/balance`);
  }

  /** POST /addons/:slug/purchase — purchase addon credits (min 5 per purchase) */
  purchaseAddonCredits(slug: string, count: number) {
    if (count < 5) {
      return Promise.reject({ statusCode: 400, message: 'Minimum purchase is 5 credits.' } as import('@/types').ApiError);
    }
    return this.request<import('@/types').AddonCreditBalance>(`/addons/${slug}/purchase`, {
      method: 'POST',
      body: JSON.stringify({ count }),
    });
  }

  /** GET /addons/:slug/ledger — credit usage history */
  getAddonLedger(slug: string, page = 1, limit = 50) {
    return this.request<{ entries: import('@/types').AddonCreditLedgerEntry[]; total: number; page: number; pages: number }>(
      `/addons/${slug}/ledger?page=${page}&limit=${limit}`,
    );
  }

  getOrganisations(params?: { page?: number; limit?: number; search?: string }) {
    const q = new URLSearchParams();
    if (params?.page) q.set('page', String(params.page));
    if (params?.limit) q.set('limit', String(params.limit));
    if (params?.search) q.set('search', params.search);
    return this.request<{ data: Organisation[]; total: number; page: number; limit: number; pages: number; hasNext: boolean; hasPrev: boolean }>(`/organisations?${q}`);
  }

  getMyApps() {
    return this.request<Array<{ id: string; name: string; slug: string; callbackUrl: string; logoUrl: string | null }>>('/auth/my-apps');
  }

  async uploadApplicationLogo(id: string, file: File): Promise<Application> {
    const form = new FormData();
    form.append('logo', file);
    // Do NOT set Content-Type — browser must set it with the multipart boundary
    const headers: Record<string, string> = {};
    if (this.accessToken) headers['Authorization'] = `Bearer ${this.accessToken}`;
    const res = await fetch(`${API_BASE}/applications/${id}/logo`, {
      method: 'POST',
      headers,
      body: form,
    });
    if (!res.ok) {
      const body = await res.json().catch(() => null);
      const message =
        (body as { error?: { message?: string } } | null)?.error?.message ??
        (body as { message?: string } | null)?.message ??
        res.statusText;
      throw { statusCode: res.status, message };
    }
    const json = await res.json();
    if (json && typeof json === 'object' && json.success === true && 'data' in json) {
      return json.data as Application;
    }
    return json as Application;
  }

  authorize(appSlug: string) {
    return this.request<{ redirectUrl: string }>('/auth/authorize', {
      method: 'POST',
      body: JSON.stringify({ appSlug }),
    });
  }

  // ── OAuth 2.1 consent & connected-apps (MCP Authorization Server) ──
  // Session-authenticated, frontend-facing endpoints. They use the repo's
  // standard `{ success, data }` envelope (per backend spec §10/D8), so they
  // go through request() and benefit from its unwrapping + auto-refresh.
  // Contract: MCP_OAUTH_AS_SPEC_FRONTEND.md §8.1 / §8.5.

  // The opaque consent_request_id is the ONLY input — raw OAuth params are never read.
  getConsentContext(consentRequestId: string) {
    return this.request<ConsentContext>(
      `/oauth/consent/context?consent_request_id=${encodeURIComponent(consentRequestId)}`,
    );
  }

  decideConsent(consentRequestId: string, decision: 'approve' | 'deny') {
    return this.request<ConsentDecisionResponse>('/oauth/consent/decide', {
      method: 'POST',
      body: JSON.stringify({ consent_request_id: consentRequestId, decision }),
    });
  }

  getGrants() {
    return this.request<OAuthGrant[]>('/oauth/grants');
  }

  revokeGrant(id: string) {
    return this.request<void>(`/oauth/grants/${id}`, { method: 'DELETE' });
  }

  // ── Free trials (PLATFORM_ADMIN) ──

  /** GET /admin/trial-settings — global default + per-app trial duration overrides. */
  getTrialSettings() {
    return this.request<import('@/types').TrialSettings>('/admin/trial-settings');
  }

  /**
   * PUT /admin/trial-settings — update the global default and/or per-app overrides.
   * A per-app `trialDurationDays` of null clears the override (falls back to the default).
   */
  updateTrialSettings(body: {
    defaultDurationDays?: number;
    perApp?: Array<{ appId: string; trialDurationDays: number | null }>;
  }) {
    return this.request<import('@/types').TrialSettings>('/admin/trial-settings', {
      method: 'PUT',
      body: JSON.stringify(body),
    });
  }

  /** GET /admin/orgs-overview — paginated all-orgs trials tracking overview. */
  getOrgsOverview(params?: { page?: number; limit?: number; search?: string; paid?: boolean }) {
    const q = new URLSearchParams();
    if (params?.page) q.set('page', String(params.page));
    if (params?.limit) q.set('limit', String(params.limit));
    if (params?.search) q.set('search', params.search);
    if (params?.paid) q.set('paid', 'true');
    return this.request<{
      data: import('@/types').OrgOverview[];
      total: number;
      page: number;
      limit: number;
      pages: number;
      hasNext: boolean;
      hasPrev: boolean;
    }>(`/admin/orgs-overview?${q}`);
  }

  /** GET /admin/orgs/:orgId/billing — itemised billing quote for an org's paid apps. */
  getOrgBilling(orgId: string) {
    return this.request<import('@/types').OrgBilling>(`/admin/orgs/${orgId}/billing`);
  }

  /** POST /admin/orgs/:orgId/convert — mark an org as a paid customer across its apps. */
  convertOrgToPaid(orgId: string, appIds?: string[]) {
    // Fastify requires a non-empty body when Content-Type: application/json is set
    return this.request<{ converted: number; apps: import('@/types').OrgOverviewApp[] }>(
      `/admin/orgs/${orgId}/convert`,
      { method: 'POST', body: appIds ? JSON.stringify({ appIds }) : '{}' },
    );
  }

  /** POST /admin/orgs/:orgId/suspend — access-suspend an org (blocks all app access until restored). */
  suspendOrg(orgId: string) {
    // Fastify requires a non-empty body when Content-Type: application/json is set.
    return this.request<{ success: true }>(`/admin/orgs/${orgId}/suspend`, {
      method: 'POST',
      body: '{}',
    });
  }

  /** POST /admin/orgs/:orgId/restore — lift a prior access-suspend, restoring app access. */
  restoreOrg(orgId: string) {
    // Fastify requires a non-empty body when Content-Type: application/json is set.
    return this.request<{ success: true }>(`/admin/orgs/${orgId}/restore`, {
      method: 'POST',
      body: '{}',
    });
  }

  /** POST /admin/orgs/:orgId/apps/:appId/suspend — block one app for an org until restored. */
  suspendOrgApp(orgId: string, appId: string) {
    // Fastify requires a non-empty body when Content-Type: application/json is set.
    return this.request<{ success: true }>(`/admin/orgs/${orgId}/apps/${appId}/suspend`, {
      method: 'POST',
      body: '{}',
    });
  }

  /** POST /admin/orgs/:orgId/apps/:appId/restore — lift a per-app suspend for an org. */
  restoreOrgApp(orgId: string, appId: string) {
    // Fastify requires a non-empty body when Content-Type: application/json is set.
    return this.request<{ success: true }>(`/admin/orgs/${orgId}/apps/${appId}/restore`, {
      method: 'POST',
      body: '{}',
    });
  }

  /** GET /trials — the caller org's own per-app trial rows (read-only; org-admin dashboard). */
  getTrials() {
    return this.request<{ trials: import('@/types').OrgTrial[] }>(`/trials`);
  }

  /** GET /admin/orgs/:orgId/trials — a specific org's existing per-app trial rows. */
  getOrgTrials(orgId: string) {
    return this.request<{ trials: import('@/types').OrgTrial[] }>(`/admin/orgs/${orgId}/trials`);
  }

  /**
   * PUT /admin/orgs/:orgId/trials/:appId — set or override a specific org's trial for an app.
   * `durationDays: null` clears the per-org override; `status: 'CONVERTED'` marks the org entitled.
   * Creates the trial row if it does not exist.
   */
  updateOrgTrial(
    orgId: string,
    appId: string,
    body: { durationDays?: number | null; extendDays?: number; status?: 'ACTIVE' | 'EXPIRED' | 'CONVERTED' },
  ) {
    return this.request<unknown>(`/admin/orgs/${orgId}/trials/${appId}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    });
  }

  // ─── Data Rights (PLATFORM_ADMIN) ─────────────────────────────────────────

  /** GET /admin/data-rights — fan-out across all registered apps */
  listAllDataRights(params?: { status?: string; requestType?: string; page?: number; limit?: number }) {
    const qs = new URLSearchParams();
    if (params?.status)      qs.set('status', params.status);
    if (params?.requestType) qs.set('requestType', params.requestType);
    if (params?.page)        qs.set('page', String(params.page));
    if (params?.limit)       qs.set('limit', String(params.limit));
    return this.request<{ apps: import('@/types').AppDataRightsResult[] }>(`/admin/data-rights?${qs}`);
  }

  /** GET /admin/data-rights/:appSlug — requests for one specific app */
  listAppDataRights(appSlug: string, params?: { status?: string; requestType?: string; page?: number; limit?: number }) {
    const qs = new URLSearchParams();
    if (params?.status)      qs.set('status', params.status);
    if (params?.requestType) qs.set('requestType', params.requestType);
    if (params?.page)        qs.set('page', String(params.page));
    if (params?.limit)       qs.set('limit', String(params.limit));
    return this.request<import('@/types').DataRightsList>(`/admin/data-rights/${appSlug}?${qs}`);
  }

  /** PUT /admin/data-rights/:appSlug/:id/status */
  updateDataRightsStatus(
    appSlug: string,
    id: string,
    body: { status: string; adminNotes?: string; rejectionReason?: string },
  ) {
    return this.request<import('@/types').DataRightsRequest>(`/admin/data-rights/${appSlug}/${id}/status`, {
      method: 'PUT',
      body: JSON.stringify(body),
    });
  }

  /** POST /admin/data-rights/:appSlug/:id/process-erasure */
  processDataRightsErasure(appSlug: string, id: string) {
    return this.request<import('@/types').DataRightsRequest>(`/admin/data-rights/${appSlug}/${id}/process-erasure`, {
      method: 'POST',
      body: '{}',
    });
  }

  // ─── DPDP: self-service privacy/consent settings ──────────────────────────

  /** GET /auth/me/consents — the authenticated user's current consent state, per type. */
  getMyConsents() {
    return this.request<MyConsents>('/auth/me/consents');
  }

  /** PATCH /auth/me/consents — grant or withdraw marketing consent. */
  updateMyConsents(marketingConsent: boolean) {
    return this.request<MyConsents>('/auth/me/consents', {
      method: 'PATCH',
      body: JSON.stringify({ marketingConsent }),
    });
  }

  // ─── DPDP: self-service data-rights requests (auth-service's own user data) ──

  /** POST /auth/data-rights/request — submit an access/correction/erasure request. */
  submitDataRightsRequest(type: SelfServiceRequestType, details?: string) {
    return this.request<SelfServiceDataRightsRequest>('/auth/data-rights/request', {
      method: 'POST',
      body: JSON.stringify({ type, ...(details ? { details } : {}) }),
    });
  }

  /** GET /auth/data-rights/my-requests — the authenticated user's own past requests. */
  getMyDataRightsRequests() {
    return this.request<SelfServiceDataRightsRequest[]>('/auth/data-rights/my-requests');
  }

  /**
   * PLATFORM_ADMIN triage of self-service requests against auth-service's own
   * user data. Distinct from listAllDataRights()/listAppDataRights(), which
   * fan out to each downstream app's own data-rights service.
   */
  listSelfServiceDataRightsRequests() {
    return this.request<SelfServiceDataRightsRequestAdmin[]>('/auth/admin/data-rights-requests');
  }

  updateSelfServiceDataRightsRequestStatus(id: string, status: SelfServiceRequestStatus) {
    return this.request<SelfServiceDataRightsRequestAdmin>(`/auth/admin/data-rights-requests/${id}`, {
      method: 'PATCH',
      body: JSON.stringify({ status }),
    });
  }
}

export const api = new ApiClient();
